/**
 * Button component - Mobile-first con touch target adecuado
 * Mínimo 48px de altura para cumplir con accesibilidad táctil
 */

import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils';

const buttonVariants = cva(
    'relative inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/35',
    {
        variants: {
            variant: {
                primary: 'bg-primary text-white hover:bg-primary-dark',
                secondary: 'border border-border bg-surface text-text hover:bg-hover',
                ghost: 'bg-transparent text-primary hover:bg-primary/10',
            },
            size: {
                small: 'min-h-9 px-4 text-sm',
                medium: 'min-h-11 px-5 text-[0.9375rem]',
                default: 'min-h-12 px-6 text-base',
                large: 'min-h-14 px-8 text-lg',
            },
            fullWidth: {
                true: 'w-full',
                false: '',
            },
            isLoading: {
                true: 'cursor-wait',
                false: '',
            },
        },
        defaultVariants: {
            variant: 'primary',
            size: 'default',
            fullWidth: false,
            isLoading: false,
        },
    }
);

interface ButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    children: ReactNode;
    isLoading?: boolean;
    ref?: Ref<HTMLButtonElement>;
}

export function Button({
    children,
    variant = 'primary',
    size = 'default',
    isLoading = false,
    fullWidth = false,
    disabled,
    className = '',
    ref,
    ...props
}: ButtonProps) {
    return (
        <button
            className={cn(
                buttonVariants({
                    variant,
                    size,
                    fullWidth,
                    isLoading,
                }),
                className
            )}
            disabled={disabled || isLoading}
            ref={ref}
            {...props}
        >
            {isLoading ? (
                <span
                    className="absolute h-5 w-5 animate-spin rounded-full border-2 border-transparent border-t-current"
                    aria-hidden="true"
                />
            ) : null}
            <span className={cn('inline-flex items-center justify-center gap-2', isLoading && 'invisible')}>
                {children}
            </span>
        </button>
    );
}
